<!-- Copyright (c) 2022-2024, NVIDIA CORPORATION & AFFILIATES. All rights reserved. -->

<template>
    <div id="home" :key="migrationId">
        <curtain :closed="curtainClosed" />
        <div v-if="!started" class="start">
            <img id="hero-img" src="../assets/algorithm.png" alt="Migration icon" />
            <h2 class="mt-0">
                Ready to start using NVUE but not sure where to start? We're here to help!
            </h2>
            <h4>
                This tool is intended to accelerate the configuration conversion process.<br />
                The outputs from this tool are not perfect but are intended to serve as a starting
                point for conversion to Cumulus Linux and NVUE.<br />
                Please work with your NVIDIA Solution Architect for any additional migration
                concerns.
            </h4>
            <h4 class="mb-40">
                Disclaimer: You should ALWAYS test the generated configurations prior to deployment
                in production.
            </h4>
            <h4 v-if="isStaff === true">NVIDIA Employees:
                <a href="https://docs.google.com/document/d/1RSCZJgolaaeTHwOtFCDyi_HPAFRwTCyT6NU1RIdmTv0" target="_blank">Click here</a>
                for a migration guide
            </h4>
            <start-instructions />
        </div>
        <dropzone v-if="!artifacts && !loading"
            v-bind="dropzoneConfig"
            v-on:addedFile="onAddedFile"
            v-on:error="onError"
            v-on:uploadSuccess="uploadSuccess" />
        <cumulus-loading-dots v-if="loading" class="mt-40" />
        <div v-if="uploading" class="mt-40">
            <cumulus-loading-dots />
            <h4>Uploading support file</h4>
        </div>
        <h4 v-if="uploadError">
            An error occurred while uploading the support file. Please try again.<br /><br />
            If the error continues, please contact us at
            <a href="mailto:air-support@nvidia.com">air-support@nvidia.com.</a>
        </h4>
        <progress-bar class="mt-40" />
        <transition name="fade">
            <status-message v-if="updateStatusId"
                class="mt-40 status-message"
                :statusId="updateStatusId"
                v-on:progress="onProgress"
                v-on:data="onData" />
        </transition>
        <transition name="fade">
            <div v-if="artifacts" >
                <artifact-table :filename="filename"
                    :artifacts="artifacts"
                    :error="error"
                    :simUrl="simUrl"
                />
                <button id="start-over-btn" @click="startOver">Start Over</button>
            </div>
        </transition>
    </div>
</template>

<script>
import ArtifactTable from '../components/ArtifactTable.vue';
import Curtain from '../components/Curtain.vue';
import Dropzone from '../components/Dropzone.vue';
import ProgressBar from '../components/ProgressBar.vue';
import StartInstructions from '../components/StartInstructions.vue';
import StatusMessage from '../components/StatusMessage.vue';

export default {
    name: 'Home',

    components: {
        ArtifactTable,
        Curtain,
        Dropzone,
        ProgressBar,
        StartInstructions,
        StatusMessage,
    },

    data() {
        return {
            artifacts: undefined,
            curtainClosed: false,
            dropzoneConfig: {
                acceptedFiles: 'application/tar,application/x-tar,.txz,.cfg,text/plain',
                placeholder: '<div class="placeholder-icon nvidia-green">+</div>'
                             + 'Upload a file from your switch here.<br /><br />We\'ll '
                             + 'convert your configuration to its NVUE equivalent automatically.',
            },
            error: undefined,
            filename: undefined,
            loading: true,
            simUrl: undefined,
            started: false,
            migrationId: 1,
            updateStatusId: undefined,
            uploadError: false,
            uploading: false,
        };
    },

    async created() {
        let data;
        try {
            const res = await fetch(`${window.airschipp.api}/v1/migrate`);
            data = await res.json();
        } catch (err) {
            console.error(err);
        }
        this.loading = false;
        if (data.cl_version) {
            this.dropzoneConfig.placeholder += '<br /><br />NVUE Configurations from this tool are currently '
                + `generated for: <br />Cumulus Linux v${data.cl_version}`;
        }
    },

    computed: {
        isStaff() {
            try {
                return JSON.parse(window.localStorage.getItem('isStaff'));
            } catch {
                return false;
            }
        },
    },

    methods: {
        onAddedFile(file) {
            this.filename = file;
            this.curtainClosed = true;
            this.started = true;
            this.uploading = true;
            // We can't use vue transitions to fade the dropzone because we don't want it
            // to re-render. Instead, briefly put up an opaque curtain while the DOM shuffles around
            // and fade that in instead.
            setTimeout(() => {
                this.curtainClosed = false;
            }, 100);
        },
        onData(data) {
            this.artifacts = data.artifacts;
            this.error = data.error;
            this.simUrl = data.simulation;
        },
        onError() {
            this.uploadError = true;
            this.uploading = false;
        },
        onProgress(percent) {
            this.$Progress.set(percent);
            if (percent >= 100) {
                this.$Progress.finish();
            }
        },
        setComponentData() {
            this.artifacts = undefined;
            this.error = undefined;
            this.started = false;
            this.updateStatusId = undefined;
            this.uploadError = false;
            this.uploading = false;
        },
        startOver() {
            this.curtainClosed = true;
            this.setComponentData();
            this.migrationId += 1; // forces DOM to re-render
            this.curtainClosed = false;
        },
        uploadSuccess(res) {
            this.uploading = false;
            this.updateStatusId = res.migration_id;
        },
    },
};
</script>
<style lang="scss">
h4 {
    font-size: 18px;

    a {
        font-weight: bold;
    }
}
#hero-img {
    height: 128px;
}
#start-over-btn {
    width: 50%;
    margin: 40px auto;
    display: block;
}
.dropzone {
    height: 60%;

    .dz-button {
        width: 50%;
        flex-direction: column;

        .placeholder-icon {
            font-size: 96px;
            margin-top: -20px;
        }
    }
}
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
.status-message {
    font-weight: bold;
}
.tooltip {
    position: relative;
}
.tooltiptext {
    width: 400px;
    text-align: center;
    border-radius: 5px;
    color: white;
    background-color: #1a1a1a;
    position: absolute;
    top: 15px;
    right: -10px;
}
</style>
